import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { EventParticipationAnswerCreationModel } from 'src/app/models/event-participations/event-participation-answer-creation.model';
import { EventParticipationQuestionModel } from 'src/app/models/event-participations/event-participation-question.model';
import { ParticipationQuestionTypeModel } from 'src/app/models/event-participations/participation-question-type.model';
import { DateSelectionComponent } from '../shared/date-selection/date-selection.component';
import validationEngine from 'devextreme/ui/validation_engine';

@Component({
  selector: 'app-answer-training-questions-dialog',
  standalone: false,
  templateUrl: './answer-training-questions-dialog.component.html',
  styleUrl: './answer-training-questions-dialog.component.scss',
})
export class AnswerTrainingQuestionsDialogComponent {
  @ViewChildren(DxFormComponent) dxForms!: QueryList<DxFormComponent>;
  
  @ViewChildren(DateSelectionComponent)
  dateSelections!: QueryList<DateSelectionComponent>;

  @Input() 
  questions: EventParticipationQuestionModel[] = [];

  @Input()
  remainingParticipantSlots: number | null = null;

  answers: EventParticipationAnswerCreationModel[] = [];

  participationTypes = ParticipationQuestionTypeModel;
  showHotelDatesSelection: boolean = false;

  yesNoOptions = [
    { 
      label: this.translateService.instant('COMMON.YES'), 
      value: true 
    },
    { 
      label: this.translateService.instant('COMMON.NO'), 
      value: false 
    }
  ];

  additionalPersonsEditorOptions: any[] = [];
  hotelBookingEditorOptions: any[] = [];

  isMobile: boolean = false;

  constructor(
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    this.checkScreenSize();
    window.addEventListener('resize', () => this.checkScreenSize());

    this.questions.forEach(q => {
      if(q.translationKey) {
        q.question = this.translateService.instant(q.translationKey);

        q.options?.forEach(o => {
          if(o.translationKey) {
            o.value = this.translateService.instant(o.translationKey);
          }
        });
      }
    });
    
    if (this.questions?.length) {
      this.answers = this.questions.map(q => ({
        questionId: q.id,
        freeTextAnswer: null,
        yesNoAnswer: null,
        startHotelDate: null,
        endHotelDate: null,
        selectedOptionIds: [],
        additionalParticipants: [],
        questionText: q.question,
        additionalParticipantsCount: 0
      }));
    }

    this.setDropdownOptions();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 767;
  }

  setDropdownOptions() {
    this.hotelBookingEditorOptions = this.answers.map(answer => ({
      items: this.yesNoOptions,
      displayExpr: 'label',
      valueExpr: 'value',
      placeholder: this.translateService.instant('TRAINING-QUESTIONNAIRE.ANSWER.SELECT-YES-NO'),
      onValueChanged: (e: any) => this.onHotelBookingChanged(answer, e.value)
    }));


    this.additionalPersonsEditorOptions = this.answers.map((answer, i) => {
      const question = this.questions[i];
      let items = [{}];

      if (question.type === ParticipationQuestionTypeModel.AdditionalPersons) {
        items = [{ 
          label: this.translateService.instant('COMMON.NO'), 
          value: 0 
        }];

        // subtract 1 because the current participant is already counted in remainingParticipantSlots
        const remaining = this.remainingParticipantSlots != null
          ? Math.max(0, this.remainingParticipantSlots - 1)
          : null;

        const maxAllowed = remaining != null
          ? Math.min(question.maxAdditionalPersons ?? 0, remaining)
          : (question.maxAdditionalPersons ?? 0);

        const yesText = this.translateService.instant('COMMON.YES');
        for (let n = 1; n <= maxAllowed; n++) {
          items.push({ label: `${yesText} - ${n}`, value: n });
        }
      }

      return {
        items,
        displayExpr: 'label',
        valueExpr: 'value',
        placeholder: this.translateService.instant('TRAINING-QUESTIONNAIRE.ANSWER.SELECT-OPTION'),
        onValueChanged: (e: any) => this.onAdditionalPersonsChanged(answer, e.value)
      };
    });
  }

  isYesNoSelected = (e: any) => {
    return e.value === true || e.value === false;
  };
 
  onAdditionalPersonsChanged(answer: EventParticipationAnswerCreationModel, count: number) {
    if (!answer.additionalParticipants) {
      answer.additionalParticipants = [];
    }

    const currentCount = answer.additionalParticipants.length;

    if (count > currentCount) {
      // Add new empty entries
      for (let i = currentCount; i < count; i++) {
        answer.additionalParticipants.push({ 
          firstName: '', 
          lastName: '' 
        });
      }
    } else if (count < currentCount) {
      // Remove extra entries from the end
      answer.additionalParticipants.splice(count, currentCount - count);
    }
  }

  onHotelBookingChanged(answer: EventParticipationAnswerCreationModel, value: boolean | null) {
    if (value) {
      this.showHotelDatesSelection = true;
    } else {
      this.showHotelDatesSelection = false;
      answer.startHotelDate = null;
      answer.endHotelDate = null;
    }
  }

  onOptionsSelected(e: any, answer: EventParticipationAnswerCreationModel) {
    if (e.node.selected) {
      answer.selectedOptionIds = [
        ...(answer.selectedOptionIds || []),
        e.node.key
      ];
    } else {
      answer.selectedOptionIds = (answer.selectedOptionIds || []).filter(
        id => id !== e.node.key
      );
    }
  }

  public validate(): boolean {
    let firstInvalidHandled = false;
    let allValid = true;

    this.dxForms.forEach(form => {
      const result = form.instance.validate();

      if (!result.isValid) {
        allValid = false;

        if (!firstInvalidHandled) {
          this.scrollToFirstBrokenRule(result);
          firstInvalidHandled = true;
        }
      }
    });

    this.dateSelections?.forEach(dateSelection => {
      const valid = dateSelection.validateDates();

      if (!valid) {
        allValid = false;
      }
    });

    const group = validationEngine.getGroupConfig('customFields');

    if (group) {
      const customFieldsResult = validationEngine.validateGroup('customFields');

      if (!customFieldsResult.isValid) {
        this.scrollToFirstBrokenRule(customFieldsResult);
        return false;
      }
    }
    
    return allValid;
  }

  private scrollToFirstBrokenRule(result: any): void {
    if (!result?.brokenRules?.length) return;

    const firstBrokenRule = result.brokenRules[0] as any;
    const element = firstBrokenRule?.validator?.$element?.();

    element?.[0]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    element?.[0]?.focus();
  }
}
